import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useT } from '../../i18n';

// The bottom sheet the chat list's actions and the mute choices use (M63),
// in the press-and-hold sheet's style (design: "Confirmations & downloads"):
// big rows, an accent icon, Close at the bottom.

export interface ChatSheetRow {
  key: string;
  label: string;
  icon?: ReactNode;
  detail?: string;
  onSelect: () => void;
  /** A switch row (e.g. Keep archived) instead of a button. */
  toggle?: { checked: boolean; hint?: string };
}

export function ChatSheet({
  isOpen,
  title,
  body,
  rows,
  label,
  onClose,
}: {
  isOpen: boolean;
  title?: string;
  body?: string;
  rows: ChatSheetRow[];
  label: string;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay bg="rgba(31, 29, 27, 0.55)" />
      <DrawerContent
        borderTopRadius="28px"
        bg="surface"
        pb="max(env(safe-area-inset-bottom), 16px)"
        aria-label={label}
        maxW={{ md: '480px' }}
        mx={{ md: 'auto' }}
      >
        <DrawerBody px={3} pt="10px" pb={0}>
          <Box
            w="40px"
            h="5px"
            borderRadius="3px"
            bg="gray.200"
            mx="auto"
            mb={2}
            aria-hidden="true"
          />
          {title && (
            <Text
              px={3}
              pt={1}
              fontFamily="heading"
              fontWeight={800}
              fontSize="20px"
              noOfLines={2}
            >
              {title}
            </Text>
          )}
          {body && (
            <Text px={3} pt={1} pb={2} fontSize="15px" color="muted">
              {body}
            </Text>
          )}
          <Box role="menu" aria-label={label}>
            {rows.map((row) =>
              row.toggle ? (
                <Box
                  key={row.key}
                  as="label"
                  w="full"
                  minH="56px"
                  px={3}
                  py={2}
                  borderRadius="16px"
                  display="flex"
                  alignItems="center"
                  gap="14px"
                  cursor="pointer"
                  _hover={{ bg: 'surface.subtle' }}
                >
                  {row.icon && (
                    <Box color="accent" flexShrink={0} aria-hidden="true">
                      {row.icon}
                    </Box>
                  )}
                  <Box flex="1" minW={0}>
                    <Text fontSize="17px" fontWeight={600}>
                      {row.label}
                    </Text>
                    {row.toggle.hint && (
                      <Text fontSize="14px" color="muted">
                        {row.toggle.hint}
                      </Text>
                    )}
                  </Box>
                  <Switch
                    size="lg"
                    isChecked={row.toggle.checked}
                    onChange={row.onSelect}
                  />
                </Box>
              ) : (
                <Box
                  key={row.key}
                  as="button"
                  type="button"
                  role="menuitem"
                  w="full"
                  h="56px"
                  px={3}
                  borderRadius="16px"
                  display="flex"
                  alignItems="center"
                  gap="14px"
                  fontSize="17px"
                  fontWeight={600}
                  textAlign="left"
                  color="ink"
                  _hover={{ bg: 'surface.subtle' }}
                  _active={{ bg: 'surface.subtle' }}
                  onClick={() => {
                    onClose();
                    row.onSelect();
                  }}
                >
                  {row.icon && (
                    <Box color="accent" flexShrink={0} aria-hidden="true">
                      {row.icon}
                    </Box>
                  )}
                  <Text as="span" flex="1">
                    {row.label}
                  </Text>
                  {row.detail && (
                    <Text
                      as="span"
                      fontSize="15px"
                      color="muted"
                      fontWeight={500}
                    >
                      {row.detail}
                    </Text>
                  )}
                </Box>
              )
            )}
          </Box>
          <Button
            w="full"
            h="52px"
            mt={2}
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            fontSize="17px"
            onClick={onClose}
          >
            {t('common.close')}
          </Button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
